import type {
  INovaCarrierData,
  INovaCompany,
  INovaPersistedWarehouse,
  INovaUser
} from '@/interfaces/nova';
import type { NovaApiService } from '@/services/nova/NovaApiService';
import { cleanWhiteSpace } from '@/services/util';
import { UserRole } from '@/interfaces/nova';
import {
  SUCCESS_IMPORT_MESSAGE,
  forEachSequentially,
  getErrorMessage,
  getFailureStatusMessage
} from './importShared';

const ROLES_WITH_WAREHOUSE_ACCESS_LIST = [
  UserRole.ADMIN,
  UserRole.OPERATOR,
  UserRole.ATTENDANT,
  UserRole.SPECTATOR
];

/** Returns a failure message when the user cannot be invited; otherwise resolves the access list ids. */
function resolveUserWarehouseAccess(
  user: INovaUser,
  existingWarehouses: INovaPersistedWarehouse[]
) {
  const accessList = user.warehouseAccessList;
  if (!accessList || accessList.length === 0) {
    return { failureMessage: null, accessListIds: null };
  }
  if (!ROLES_WITH_WAREHOUSE_ACCESS_LIST.includes(user.role)) {
    return {
      failureMessage: `User role of "${user.role}" does not support a warehouse access list.`,
      accessListIds: null
    };
  }

  const warehouseNameArr = cleanWhiteSpace(
    Array.isArray(accessList) ? accessList.join(';') : String(accessList)
  ).split(';');
  const matchingWarehouses = existingWarehouses.filter(warehouse =>
    warehouseNameArr.some(
      warehouseName =>
        warehouseName.toLowerCase().trim() === cleanWhiteSpace(warehouse.name).toLowerCase()
    )
  );
  if (matchingWarehouses.length !== warehouseNameArr.length) {
    return {
      failureMessage: `Cannot assign user to warehouse. One or more warehouses in warehouse access list do not exist in this Org. Please check the warehouse list separator, it must be a semicolon ';'.`,
      accessListIds: null
    };
  }
  return {
    failureMessage: null,
    accessListIds: matchingWarehouses.flatMap(warehouse => warehouse.id ?? [])
  };
}

async function inviteUser(
  apiService: NovaApiService,
  user: INovaUser,
  existingWarehouses: INovaPersistedWarehouse[],
  usersReport: INovaUser[]
) {
  const { failureMessage, accessListIds } = resolveUserWarehouseAccess(user, existingWarehouses);
  if (failureMessage) {
    user.wasImportedIntoNova = getFailureStatusMessage({ message: failureMessage });
    usersReport.unshift(user);
    return;
  }
  if (accessListIds) {
    user.warehouseAccessListInput = user.warehouseAccessList;
    user.warehouseAccessList = accessListIds;
  }

  try {
    await apiService.inviteUser(user);
    user.wasImportedIntoNova = SUCCESS_IMPORT_MESSAGE;
    usersReport.push(user);
  } catch (e) {
    user.wasImportedIntoNova = getFailureStatusMessage({ e });
    usersReport.unshift(user);
  } finally {
    user.warehouseAccessList = user.warehouseAccessListInput;
    delete user.warehouseAccessListInput;
  }
}

export async function inviteUsers(
  apiService: NovaApiService,
  users: INovaUser[],
  existingWarehouses: INovaPersistedWarehouse[]
) {
  const usersReport: INovaUser[] = [];
  await forEachSequentially(users, user =>
    inviteUser(apiService, user, existingWarehouses, usersReport)
  );
  return { usersReport };
}

async function createCarrierCompany(
  apiService: NovaApiService,
  carrier: INovaCarrierData,
  companiesKeyedByName: Record<string, INovaCompany>
) {
  const companyRes = await apiService.createCompany(carrier.company);
  const [company] = companyRes.data;
  if (!company) {
    throw new Error('no company was returned');
  }
  companiesKeyedByName[carrier.company] = company;
  carrier.companyId = company.id;
}

async function createCarrier(
  apiService: NovaApiService,
  carrier: INovaCarrierData,
  companiesKeyedByName: Record<string, INovaCompany>,
  carriersReport: INovaCarrierData[]
) {
  if (carrier.phone) {
    carrier.phone = String(carrier.phone);
  }
  const companyKey = Object.keys(companiesKeyedByName).find(
    company => company.toLowerCase() === carrier.company.toLowerCase()
  );
  if (companyKey) {
    carrier.companyId = companiesKeyedByName[companyKey]?.id;
  } else {
    try {
      await createCarrierCompany(apiService, carrier, companiesKeyedByName);
    } catch (e) {
      carrier.wasImportedIntoNova = getFailureStatusMessage({
        message: `Carrier company creation failed because ${getErrorMessage(e)}`
      });
      carriersReport.unshift(carrier);
      return;
    }
  }

  try {
    await apiService.createCarrier(carrier);
    carrier.wasImportedIntoNova = SUCCESS_IMPORT_MESSAGE;
    carriersReport.push(carrier);
  } catch (e) {
    let errorMessage = getErrorMessage(e);
    if (errorMessage.includes('Duplicate key value violates unique constraint')) {
      errorMessage = `user with email ${carrier.email} already exists`;
    }
    carrier.wasImportedIntoNova = getFailureStatusMessage({
      message: `Carrier creation failed because ${errorMessage}`
    });
    carriersReport.unshift(carrier);
  }
}

export async function createCarriers(
  apiService: NovaApiService,
  carriers: INovaCarrierData[],
  companiesKeyedByName: Record<string, INovaCompany>
) {
  const carriersReport: INovaCarrierData[] = [];
  await forEachSequentially(carriers, carrier =>
    createCarrier(apiService, carrier, companiesKeyedByName, carriersReport)
  );
  return { carriersReport };
}
